import {Injectable, Type} from '@angular/core';

import {CheckmarkCellComponent} from '../components/cells/checkmark-cell/checkmark-cell.component';
import {FormatBadgeCellComponent} from '../components/cells/format-badge-cell/format-badge-cell.component';
import {IconCellComponent} from '../components/cells/icon-cell/icon-cell.component';
import {ParentCellComponent} from '../components/cells/parent-cell/parent-cell.component';
import {ShortDateCellComponent} from '../components/cells/short-date-cell/short-date-cell.component';
import {ShortRecordIdCellComponent} from '../components/cells/short-record-id-cell/short-record-id-cell.component';
import {StatusCellComponent} from '../components/cells/status-cell/status-cell.component';
import {StukCountCellComponent} from '../components/cells/stuk-count-cell/stuk-count-cell.component';
import {StukFormatsCellComponent} from '../components/cells/stuk-formats-cell/stuk-formats-cell.component';
import {TextCellComponent} from '../components/cells/text-cell/text-cell.component';
import {TitleCellComponent} from '../components/cells/title-cell/title-cell.component';
import {ApiFragment} from '../entities/api-fragment.entity';
import {HierarchyColumn} from '../entities/hierarchy-column.entity';
import {DottedKey, MetadataField} from '../entities/metadata-field.entity';
import {Comparable} from '../entities/misc/comparable.interface';
import {Trackable} from '../entities/misc/trackable.interface';
import {BaseColumnComponent} from './base-column-component';

export abstract class ColumnHandlerService<T extends Comparable & Trackable> {
    private readonly fallbackComponent: Type<BaseColumnComponent<any, any>> = TextCellComponent;
    readonly components: Map<string, Type<BaseColumnComponent<any, any>>> = new Map();

    constructor() {
        this.setDefaults();
    }

    public getComponent(flatOrDottedKey: string): Type<BaseColumnComponent<T>> {
        return this.components.get(flatOrDottedKey) ?? this.fallbackComponent;
    }

    private setDefaults(): void {
        this.components.set(MetadataField.AnalogCluster, IconCellComponent);
        this.components.set(MetadataField.RecordStatus, StatusCellComponent);
        this.components.set(DottedKey.RecordStatus, StatusCellComponent);
        this.components.set(DottedKey.RecordId, ShortRecordIdCellComponent);
        this.components.set(MetadataField.UserLastModifiedDate, ShortDateCellComponent);
        this.components.set(MetadataField.Title, TitleCellComponent);
        this.components.set(MetadataField.LastModifiedDate, ShortDateCellComponent);
        this.components.set(MetadataField.ArchiveDate, ShortDateCellComponent);
        this.components.set(DottedKey.ArchiveDate, ShortDateCellComponent);
        this.components.set(MetadataField.RecordRejectionsRejectionDate, ShortDateCellComponent);
        this.components.set(MetadataField.DossierDeletionDate, ShortDateCellComponent);
        this.components.set(MetadataField.DavExpiryExpectedDate, ShortDateCellComponent);
        this.components.set(DottedKey.DossierOpeningDate, ShortDateCellComponent);
        this.components.set(DottedKey.DossierClosureDate, ShortDateCellComponent);
        this.components.set(MetadataField.DossierAnalog, CheckmarkCellComponent);
        // Safe as a default: no page outside the tree defines a column under these keys.
        this.components.set(HierarchyColumn.StukFormats, StukFormatsCellComponent);
        this.components.set(HierarchyColumn.StukCount, StukCountCellComponent);
        // A default rather than a per-handler entry, because the detail surfaces that define this column do not
        // all provide a handler of their own: the evaluate and weesdocument hosts inherit this one, and a cell
        // registered only on the archive and concept handlers would leave those two printing bare text.
        this.components.set(MetadataField.OriginalExtension, FormatBadgeCellComponent);
        this.components.set(HierarchyColumn.Parent, ParentCellComponent);
    }
}

@Injectable()
export class RecordsColumnHandlerService extends ColumnHandlerService<ApiFragment> {
    constructor() {
        super();
    }
}
